import React, { PureComponent } from "react"
import PropTypes from "prop-types"
import { withRadium, keyframes } from "higher-order-components"
import { WINDOW, IS_IOS } from "AtomicUtils/environment"
import gtmHandler from "AtomicUtils/gtmHandler"
import { A, H5, LoadingSection } from 'AtomicComponents'
/**
 * Flex Icon Link Component.
 * @param {String} name - Name Value.
 * @param {String} image - Image Url.
 * @param {String} type - Type Value.
 * @param {Url} href - Href Value.
 * @param {Boolean} external - Target is external.
 * @param {String} Interactive - Interactive Value.
 * @param {Boolean} fade - Fade Value.
 * @returns {ReactElement} React Component
 */

class NameplateLink extends PureComponent {
  constructor() {
    super()
    this.state = {
      resetAnimations: false
    }
  }
  componentDidMount() {
    /*
      This is a HACK to force the animations to re-run after mount (don't try this at home).
      Animations were not running on Safari and IE in the global nav.
      It could be a problem with radium keyframes - https://github.com/FormidableLabs/radium/issues/912
    */
    setTimeout(() => this.setState({ resetAnimations: true }), 0)
  }
  handleOnClick(e) {
    const {
      disabled,
      external,
      gtm,
      onClick,
      href
    } = this.props

    !external && e && e.preventDefault()
    if (disabled) return
    if (gtm && gtm.category) {
      // If not, use CMS gtm event
      gtmHandler({
        pageCategory: gtm.category,
        pageAction: gtm.action
      })
    }

    // onclick button is for handling GTM calls, so it should be fired wheter if we have href or not!
    if (onClick && typeof onClick === "function") {
      onClick()
    }
    if (href && !external) {
      if (IS_IOS) {
        setTimeout(() => {
          WINDOW.location.href = href
        }, 300)
      } else {
        WINDOW.location.href = href
      }
    }
  }
  render() {
    const {
      animationDelay,
      subNav,
      activate,
      reset,
      onClick,
      href,
      faded,
      disabled,
      appearDisabled,
      subheading,
      name,
      centerContent,
      type,
      image,
      fade,
      loading,
      external
    } = this.props
    return (
      <li
        data-test="flex-icon-link"
        data-status={appearDisabled || disabled ? "disabled" : "enabled"}
        style={[
          styles.base(centerContent),
          styles.animate,
          { animationDelay: `${animationDelay}s` },
          subNav ? subNavStyles.base : topNavStyles.base
        ]}
        key={this.state.resetAnimations ? "remounted" : "mounted"}
        onMouseOver={activate}
        onFocus={activate}
        onMouseOut={reset}
        onBlur={reset}>
        <A
          withoutStyle
          href={href}
          onClick={e => this.handleOnClick(e)}
          target={external ? "_blank" : undefined}>
          <div style={styles.textContent(centerContent)}>
            {image && (
              <div data-info="icon" style={[styles.icon]}>
                <div
                  style={[
                    subNav ? styles.hide : "",
                    appearDisabled || disabled || faded ? styles.fade : ""
                  ]}>
                  <img style={[styles.img, fade ? styles.fade : ""]} src={image} alt={name} />
                </div>
                {loading &&
                  <div style={{
                    position: "absolute",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                    top: "10%",
                    width: "100%"
                  }}>
                    <LoadingSection visible />
                  </div>
                }
              </div>
            )}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              flexDirection: 'column'
            }}>
              {subheading && <div style={[{ textTransform: "uppercase" }, styles.interactive]}>{subheading}</div>}
              <H5
                withDetails
                data-test="flex-title"
                style={[
                  styles.name,
                  appearDisabled || disabled || faded ? styles.fade : "",
                  subNav ? subNavStyles.name : topNavStyles.name
                ]}>
                {name}
              </H5>

              {subNav ? "" : <span style={styles.type}>{type}</span>}
            </div>
          </div>
        </A>
      </li>
    )
  }
}

NameplateLink.propTypes = {
  name: PropTypes.string.isRequired,
  image: PropTypes.string,
  type: PropTypes.node,
  href: PropTypes.string,
  external: PropTypes.bool,
  onClick: PropTypes.func,
  Interactive: PropTypes.string,
  fade: PropTypes.bool,
  loading: PropTypes.bool,
  animationDelay: PropTypes.number,
  subNav: PropTypes.bool,
  faded: PropTypes.bool,
  disabled: PropTypes.bool,
  appearDisabled: PropTypes.bool,
  activate: PropTypes.func,
  reset: PropTypes.func
}

export default withRadium()(NameplateLink)

const dropOutKeyframes = keyframes({
  from: {
    opacity: 0,
    transform: "translateY(-1em)"
  },
  to: {
    opacity: 1,
    transform: "translateY(0)"
  }
})

const styles = {
  /*
    https://welovemdp.atlassian.net/projects/MR/issues/MR-2539
    IE 11 was not respecting flex-basis, using explicit width instead.
    TODO: Consider changing component prop name to reflect this in case container components
    expect different behaviour based on flex direction.
  */
  animate: {
    animation: "x .33s 0s forwards",
    animationName: dropOutKeyframes
  },
  base: centerContent => ({
    width: "100%",
    flexGrow: 0,
    flexShrink: 1,
    padding: "0",
    display: "block",
    opacity: 0,
    boxSizing: "border-box",
    "@media (min-width: 768px) and (max-width: 1079px)": {
      width: "50%"
    },
    "@media (min-width: 900px)": {
      width: centerContent ? `33.33%` : '50%'
    },
    "@media (min-width: 1080px)": {
      width: "33%"
    },
    "*": {
      boxSizing: "border-box"
    }
  }),
  container: {
    transition: ".5s",
    display: "flex",
    textDecoration: "none",
    color: "#101010",
    alignItems: "center",
    willChange: "opacity",
    "@media (min-width: 768px)": {
      flexWrap: "wrap"
    },
    ":focus": {
      outline: 0
    }
  },
  hide: {
    display: "none"
  },
  textContent: centerContent => ({
    display: "flex",
    margin: "auto",
    flexGrow: 1,
    boxSizing: 'border-box',
    width: "100%",
    flexDirection: "row",
    padding: "3px",
    "@media (min-width: 768px)": {
      padding: ".5em 1em .5em 0",
      flexDirection: "column"
    },
    "@media (min-width: 1080px)": {
      width: centerContent ? "300px" : "100%"
    }
  }),
  fade: {
    transition: ".5s",
    opacity: 0.33
  },
  interactive: {
    letterSpacing: ".2em",
    fontSize: "70%",
    fontWeight: 600,
    marginBottom: ".25em",
    color: "#910A2D",
    minHeight: "1em",
    lineHeight: "1.3"
  },
  name: {
    textTransform: "uppercase",
    display: "block",
    margin: "0.2em auto 0.5em auto",
  },
  type: {
    color: "#999999",
    textTransform: "none",
    fontSize: "85%",
    paddingTop: '0px',
    display: "block",
    lineHeight: "1.3"
  },
  icon: {
    order: -1,
    position: "relative",
    flex: '0 0 50%',
    boxSizing: 'border-box',
    padding: '10px',
    flexGrow: 0,
    display: "flex",
    alignItems: "center",
    flexShrink: 0,
    "@media (min-width: 768px)": {
      maxWidth: "100%",
      margin: "auto",
      flex: 'none',
    }
  },
  img: {
    width: "100%",
    height: "auto",
    "@media (min-width: 768px)": {
      display: "block",
      margin: "0 auto",
      transition: "opacity .33s"
    }
  }
}

const subNavStyles = {
  base: {
    textAlign: "left",
    padding: "0 1.5em",
    "@media (min-width: 768px)": {
      minWidth: "100%",
      maxWidth: "100%"
    }
  },
  name: {
    fontSize: "80%",
    color: "#808080",
    fontWeight: 400,
    textTransform: "none",
    "@media (min-width: 768px)": {
      fontSize: "1.6vw",
      letterSpacing: 0
    }
  }
}

const topNavStyles = {
  base: {
    textAlign: "left",
    "@media (min-width: 768px)": {
      textAlign: "center"
    }
  },
  name: {
    fontSize: "94%",
    "@media (min-width: 376px) and (max-width: 768px)": {
      fontSize: "120%"
    }
  }
}



// WEBPACK FOOTER //
// ./src/components/nameplate-link/index.jsx